import React, { useState } from "react";
import { TextField, Typography, Box, FormControl, InputLabel, Select, MenuItem, FormControlLabel, FormHelperText, Switch } from "@mui/material";
import styled from "@emotion/styled";
import { PROJECT_TYPE_OPTIONS } from "../../utils/projectTypes";
import { selectableSkills, skillOptionLabel } from "../../utils/skillUtils";

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FieldPair = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
    },
}));

const IntroText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const BILLING_TYPE_OPTIONS = ["Retainer", "Hourly", "Fixed Price", "Internal", "Non Billable"];

const StepQuoteBasics = ({ form, onChange, skills = [] }) => {
    const [descriptionTouched, setDescriptionTouched] = useState(false);
    const handleField = (name) => (e) => onChange({ [name]: e.target.value });
    const handleDayCount = (name) => (e) => onChange({ [name]: Math.max(0, parseInt(e.target.value, 10) || 0) });

    const projectType = form.projectType || "";
    const isCustomProjectType = projectType !== "" && !PROJECT_TYPE_OPTIONS.includes(projectType);
    const skillId = form.skill || "";
    const showDescriptionError = descriptionTouched && !(form.description || "").trim();

    return (
        <>
            <IntroText variant="body2" color="text.secondary">
                Name this standard engagement so your team can find it, and describe it so the AI can tell it apart from the other levels of the same service.
            </IntroText>

            <FormField>
                <TextField
                    fullWidth
                    label="Name"
                    value={form.name}
                    onChange={handleField("name")}
                    required
                    autoFocus
                    placeholder="e.g. Analytics Audit — Standard"
                    helperText="Shown in the template list your team picks from."
                />
            </FormField>

            <FormField>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    value={form.description}
                    onChange={handleField("description")}
                    onBlur={() => setDescriptionTouched(true)}
                    required
                    error={showDescriptionError}
                    placeholder="e.g. A standard-depth analytics audit: tag review, funnel checks and a written findings report. Not the in-depth version — no custom dashboards or data warehouse work."
                    helperText="Required. This is what the AI matches a client's request against, so say what this level of the engagement covers and how it differs from your basic or in-depth version."
                />
            </FormField>

            <FormField>
                <FormControl fullWidth>
                    <InputLabel shrink>Project Type</InputLabel>
                    <Select value={projectType} label="Project Type" notched onChange={handleField("projectType")} displayEmpty>
                        <MenuItem value=""><em>No project type</em></MenuItem>
                        {PROJECT_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                        {isCustomProjectType && (
                            <MenuItem value={projectType}>{projectType} (custom)</MenuItem>
                        )}
                    </Select>
                    <FormHelperText>
                        {isCustomProjectType
                            ? `"${projectType}" is not one of the standard types. Leave it as it is, or pick a standard one to replace it.`
                            : "Optional. A rough category only — the description above is what the AI matches on."}
                    </FormHelperText>
                </FormControl>
            </FormField>

            <FormField>
                <FormControl fullWidth>
                    <InputLabel>Billing Type</InputLabel>
                    <Select value={form.billingType || "Fixed Price"} label="Billing Type" onChange={handleField("billingType")}>
                        {BILLING_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </FormField>

            <FormField>
                <FormControl fullWidth>
                    <InputLabel shrink>Work Type</InputLabel>
                    <Select value={skillId} label="Work Type" notched displayEmpty onChange={handleField("skill")}>
                        <MenuItem value=""><em>No work type</em></MenuItem>
                        {selectableSkills(skills, skillId).map((s) => (
                            <MenuItem key={s._id} value={s._id}>{skillOptionLabel(s)}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        Optional. The main kind of work this engagement is, used as the default on quotes drafted from this template.
                    </FormHelperText>
                </FormControl>
            </FormField>

            <FieldPair>
                <TextField
                    fullWidth
                    type="number"
                    label="Days before start"
                    value={form.daysBeforeStart ?? 0}
                    onChange={handleDayCount("daysBeforeStart")}
                    inputProps={{ min: 0 }}
                    helperText="Working days between the client accepting and the first task starting. 0 means work starts straight away."
                />
                <TextField
                    fullWidth
                    type="number"
                    label="Extra days after the last task"
                    value={form.extraDays ?? 0}
                    onChange={handleDayCount("extraDays")}
                    inputProps={{ min: 0 }}
                    helperText="Working days of breathing room added after the last task, before the date you promise the client. 0 means no padding."
                />
            </FieldPair>

            <FormField>
                <FormControlLabel
                    control={<Switch checked={form.isActive !== false} onChange={(e) => onChange({ isActive: e.target.checked })} />}
                    label={form.isActive !== false ? "Active — the AI and your team can pick this template" : "Retired — kept for reference, never picked for a new quote"}
                />
            </FormField>
        </>
    );
};

export default StepQuoteBasics;
